import { NumberInput } from "../../shared/NumberInput";
import { SegmentedControl } from "../../shared/SegmentedControl";
import { Slider } from "../../shared/Slider";
import { fmtEUR } from "../../lib/calculations";
import type { ChargesMode, SimulationInput } from "../../lib/types";

interface Props {
  input: SimulationInput;
  update: <K extends keyof SimulationInput>(k: K, v: SimulationInput[K]) => void;
  updateDetail: <K extends keyof SimulationInput["chargesDetail"]>(k: K, v: number) => void;
  chargesTotales: number;
  revenusAnnuels: number;
}

export function StepCharges({ input, update, updateDetail, chargesTotales, revenusAnnuels }: Props) {
  const d = input.chargesDetail;
  const fraisGestionEUR = (revenusAnnuels * d.fraisGestionPct) / 100;

  return (
    <div className="space-y-5">
      <header>
        <p className="text-sm font-medium text-primary">Étape 3 / 4</p>
        <h2 className="mt-1 text-2xl font-bold text-foreground">Quelles sont vos charges ?</h2>
      </header>

      <SegmentedControl<ChargesMode>
        value={input.chargesMode}
        onChange={(v) => update("chargesMode", v)}
        options={[
          { value: "simple", label: "Mode simple" },
          { value: "detaille", label: "Mode détaillé" },
        ]}
      />

      {input.chargesMode === "simple" ? (
        <div className="space-y-4">
          <Slider
            label="Charges estimées"
            value={input.chargesPct}
            onChange={(n) => update("chargesPct", n)}
            min={15}
            max={60}
            step={1}
            suffix="%"
            display={`${input.chargesPct}% ≈ ${fmtEUR(chargesTotales)}/an`}
            showRange
            helper="30% est la moyenne pour un LMNP classique. 40%+ si charges de copro élevées ou gestion déléguée."
            helpContent={
              <div className="space-y-2">
                <p className="font-medium text-foreground">Les charges incluent généralement :</p>
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                  <li>Taxe foncière</li>
                  <li>Assurance PNO</li>
                  <li>Charges de copropriété non récupérables</li>
                  <li>Entretien et petites réparations</li>
                  <li>CFE (cotisation foncière)</li>
                  <li>Frais de gestion si agence</li>
                </ul>
                <p className="pt-1 text-foreground">
                  <strong>30%</strong> est une bonne moyenne pour un LMNP classique avec gestion en direct.
                </p>
              </div>
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberInput
              label="Taxe foncière"
              value={d.taxeFonciere}
              onValueChange={(n) => updateDetail("taxeFonciere", n)}
              suffix="€/an"
            />
            <NumberInput
              label="Assurance PNO"
              value={d.assurancePNO}
              onValueChange={(n) => updateDetail("assurancePNO", n)}
              suffix="€/an"
              helper="Propriétaire Non Occupant"
            />
            <NumberInput
              label="Charges copro non récup."
              value={d.chargesCopro}
              onValueChange={(n) => updateDetail("chargesCopro", n)}
              suffix="€/an"
            />
            <NumberInput
              label="Entretien & réparations"
              value={d.entretien}
              onValueChange={(n) => updateDetail("entretien", n)}
              suffix="€/an"
            />
            <NumberInput
              label="CFE"
              value={d.cfe}
              onValueChange={(n) => updateDetail("cfe", n)}
              suffix="€/an"
              helper="Cotisation Foncière des Entreprises, ~200-500€"
            />
            <NumberInput
              label="Comptabilité"
              value={d.comptabilite}
              onValueChange={(n) => updateDetail("comptabilite", n)}
              suffix="€/an"
              helper="Optionnel mais déductible, ~400-800€"
            />
            <div className="sm:col-span-2">
              <Slider
                label="Frais de gestion locative"
                value={d.fraisGestionPct}
                onChange={(n) => updateDetail("fraisGestionPct", n)}
                min={0}
                max={15}
                step={0.5}
                suffix="%"
                display={`${d.fraisGestionPct}% ≈ ${fmtEUR(fraisGestionEUR)}/an`}
                helper="Si agence, 6-10% des loyers"
              />
            </div>
            <NumberInput
              label="Autres"
              value={d.autres}
              onValueChange={(n) => updateDetail("autres", n)}
              suffix="€/an"
            />
          </div>
          <button
            type="button"
            onClick={() => update("chargesMode", "simple")}
            className="text-xs text-muted-foreground underline hover:text-primary"
          >
            ← Réinitialiser en mode simple
          </button>
        </div>
      )}

      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4" role="status">
        <p className="text-sm text-foreground">
          Charges totales annuelles :{" "}
          <strong className="tabular font-bold">{fmtEUR(chargesTotales)}</strong>
        </p>
      </div>
    </div>
  );
}
